import {
  Paper,
  Typography,
  Box,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import {
  FaIdCard,
  FaFileAlt,
  FaFilePdf,
  FaFileSignature,
  FaUserCircle,
  FaUserEdit,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
} from "react-icons/fa";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage, auth } from "../firebase";

const DOCS = [
  { key: "curp", label: "CURP", icon: <FaIdCard /> },
  { key: "acta_nacimiento", label: "Acta de Nacimiento", icon: <FaFileAlt /> },
  { key: "ine", label: "INE", icon: <FaIdCard /> },
  { key: "cv", label: "Currículum Vitae", icon: <FaFilePdf /> },
  { key: "nss", label: "Número de Seguridad Social", icon: <FaFileSignature /> },
  { key: "constancia", label: "Constancia de Estudios/Trabajo", icon: <FaFileAlt /> },
  { key: "foto", label: "Foto", icon: <FaUserCircle /> },
  { key: "certificado_medico", label: "Certificado Médico", icon: <FaUserEdit /> },
];

const colorRojo = "#ff3333";

/**
 * Props:
 *   docStates  – { curp: { status: "aprobado"|"pendiente"|"rechazado", url, motivo }, … }
 *                Viene de MiPerfil.js (memo docStates). Vacío = primer carga del usuario.
 *   onSuccess  – () => void  — llamado ~1.2s después de guardar con éxito.
 */
export default function FormDocumentos({ docStates = {}, onSuccess }) {
  const [sobreMi, setSobreMi] = useState("");
  const [archivos, setArchivos] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Diálogo de confirmación para documentos pendientes
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingLabels, setPendingLabels] = useState([]);

  const handleFileChange = (key, e) => {
    setArchivos((prev) => ({ ...prev, [key]: e.target.files[0] }));
  };

  const uploadFile = async (file, path) => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  // ── Subida real a Firebase + POST al backend ────────────────────────────
  const doUpload = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    const user = auth.currentUser;
    if (!user) {
      setError("Usuario no autenticado. Inicia sesión e intenta de nuevo.");
      setLoading(false);
      return;
    }

    try {
      const token = await user.getIdToken();
      const userId = user.uid;

      // Solo subir docs con archivo seleccionado y que NO estén validados
      const docsToUpload = DOCS.filter(
        ({ key }) => archivos[key] && docStates[key]?.status !== "aprobado"
      );

      if (docsToUpload.length === 0 && !sobreMi.trim()) {
        setError("Selecciona al menos un documento o completa el campo Sobre mí.");
        setLoading(false);
        return;
      }

      // Subir a Firebase Storage en paralelo
      const urls = await Promise.all(
        docsToUpload.map(({ key }) =>
          uploadFile(
            archivos[key],
            `usuarios/${userId}/${key}-${Date.now()}-${archivos[key].name}`
          )
        )
      );

      // Construir payload: solo los docs subidos + sobre_mi
      const payload = { user_id: userId, sobre_mi: sobreMi };
      docsToUpload.forEach(({ key }, i) => {
        payload[`${key}_url`] = urls[i] || null;
      });

      const res = await fetch("https://vol-backend.onrender.com/documentos", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Guardia del backend: documento ya validado
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          if (body.code === "DOC_ALREADY_VALIDATED") {
            setError(
              `No puedes reemplazar documentos ya validados: ${(body.blocked || []).join(", ")}.`
            );
            return;
          }
        }
        throw new Error("Error al guardar los documentos");
      }

      setSuccess("Documentos guardados con éxito.");
      setArchivos({});
      setSobreMi("");
      // Esperar un momento para que el usuario vea el mensaje antes de cerrar
      setTimeout(() => onSuccess?.(), 1200);
    } catch (err) {
      console.error(err);
      setError(
        "No se pudo guardar, intenta de nuevo más tarde. Si el problema persiste, contacta a plataformacrmsn@gmail.com."
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Submit: detecta pendientes y muestra confirmación si aplica ─────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const pendientes = DOCS.filter(
      ({ key }) => archivos[key] && docStates[key]?.status === "pendiente"
    ).map(({ label }) => label);

    if (pendientes.length > 0) {
      setPendingLabels(pendientes);
      setConfirmOpen(true);
      return;
    }

    await doUpload();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    await doUpload();
  };

  // ── Render por documento ────────────────────────────────────────────────
  const renderDocButton = ({ key, label, icon }) => {
    const state = docStates[key];
    const status = state?.status; // "aprobado" | "pendiente" | "rechazado" | undefined

    // CASO A: Validado — bloqueado
    if (status === "aprobado") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Button
            variant="contained"
            fullWidth
            disabled
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              fontSize: 14,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              "&.Mui-disabled": {
                backgroundColor: "#4caf5033",
                color: "#2e7d3299",
              },
            }}
          >
            <FaCheckCircle style={{ color: "#2e7d32", opacity: 0.7 }} />
            {label}
          </Button>
          <Typography variant="caption" sx={{ color: "#2e7d32", mt: 0.5, display: "block" }}>
            ✅ Validado — no se puede reemplazar
          </Typography>
        </Grid>
      );
    }

    // CASO C: Rechazado — muestra motivo + botón rojo "Volver a subir"
    if (status === "rechazado") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{
              color: "#fff",
              backgroundColor: "#c62828",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: 14,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              "&:hover": { backgroundColor: "#a81f1f" },
              svg: { color: "#fff" },
            }}
          >
            <FaTimesCircle />
            {label} — Volver a subir
            <input
              type="file"
              hidden
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => handleFileChange(key, e)}
            />
          </Button>
          {state?.motivo && (
            <Typography variant="caption" sx={{ color: "#c62828", mt: 0.5, display: "block" }}>
              ❌ Motivo de rechazo: {state.motivo}
            </Typography>
          )}
          {archivos[key] && (
            <Typography variant="caption" sx={{ color: colorRojo, mt: 0.5, display: "block" }}>
              📎 {archivos[key].name}
            </Typography>
          )}
        </Grid>
      );
    }

    // CASO B: Pendiente — botón amarillo, avisa que está en revisión
    if (status === "pendiente") {
      return (
        <Grid item xs={12} sm={6} key={key}>
          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{
              color: "#fff",
              backgroundColor: "#f59e0b",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: 14,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              "&:hover": { backgroundColor: "#d97706" },
              svg: { color: "#fff" },
            }}
          >
            <FaClock />
            {icon}
            {label}
            <input
              type="file"
              hidden
              accept="image/*,.pdf,.doc,.docx"
              onChange={(e) => handleFileChange(key, e)}
            />
          </Button>
          <Typography variant="caption" sx={{ color: "#b45309", mt: 0.5, display: "block" }}>
            🕐 En revisión — puedes reemplazarlo
          </Typography>
          {archivos[key] && (
            <Typography variant="caption" sx={{ color: colorRojo, mt: 0.5, display: "block" }}>
              📎 {archivos[key].name}
            </Typography>
          )}
        </Grid>
      );
    }

    // CASO D: Sin documento — botón normal rojo
    return (
      <Grid item xs={12} sm={6} key={key}>
        <Button
          variant="contained"
          component="label"
          fullWidth
          sx={{
            color: "#fff",
            backgroundColor: colorRojo,
            textTransform: "none",
            fontWeight: "bold",
            fontSize: 14,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: "none",
            "&:hover": { backgroundColor: "#cc2929" },
            svg: { color: "#fff" },
          }}
        >
          {icon}
          {label}
          <input
            type="file"
            hidden
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => handleFileChange(key, e)}
          />
        </Button>
        {archivos[key] && (
          <Typography variant="caption" sx={{ color: colorRojo, mt: 0.5, display: "block" }}>
            📎 {archivos[key].name}
          </Typography>
        )}
      </Grid>
    );
  };

  // ── JSX principal ───────────────────────────────────────────────────────
  return (
    <Paper
      elevation={8}
      sx={{
        p: 4,
        maxWidth: 700,
        margin: "auto",
        mt: 6,
        backgroundColor: "#ffffff",
        borderRadius: 3,
        boxShadow: `0 0 15px 3px ${colorRojo}88`,
        overflow: "visible",
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        color="#000000"
        mb={1}
        textAlign="center"
        sx={{
          textTransform: "uppercase",
          borderBottom: `2px solid ${colorRojo}`,
          pb: 0.5,
          mb: 2,
          letterSpacing: 1,
        }}
      >
        Completa tu perfil
      </Typography>

      <Typography
        variant="body1"
        color="#000"
        fontWeight="regular"
        textAlign="center"
        sx={{ mb: 3 }}
      >
        Cuéntanos más sobre ti
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          backgroundColor: "#fff",
          p: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <TextField
          label="Sobre mí"
          multiline
          minRows={4}
          value={sobreMi}
          onChange={(e) => setSobreMi(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: colorRojo },
              "&:hover fieldset": { borderColor: colorRojo },
              "&.Mui-focused fieldset": { borderColor: colorRojo },
            },
          }}
        />

        <Typography
          variant="body1"
          color="#000"
          fontWeight="regular"
          textAlign="center"
          sx={{ mt: 2, mb: 3 }}
        >
          Sube tus documentos
        </Typography>

        <Grid container spacing={2}>
          {DOCS.map((doc) => renderDocButton(doc))}
        </Grid>

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            mt: 3,
            backgroundColor: colorRojo,
            "&:hover": { backgroundColor: "#cc2929" },
            fontWeight: "bold",
            fontSize: 16,
            color: "#fff",
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Guardar información"
          )}
        </Button>
      </Box>

      {/* Diálogo de confirmación — reemplazar docs pendientes */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          ¿Reemplazar documentos pendientes?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "#555" }}>
            Los siguientes documentos ya están en revisión y serán reemplazados:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {pendingLabels.map((label) => (
              <Chip
                key={label}
                icon={<FaClock />}
                label={label}
                sx={{
                  backgroundColor: "#fff8e1",
                  border: "1px solid #f59e0b88",
                  color: "#7c5800",
                  fontWeight: 700,
                  "& .MuiChip-icon": { color: "#b45309" },
                }}
              />
            ))}
          </Box>
          <Typography variant="body2" sx={{ color: "#888", mt: 1 }}>
            El documento anterior quedará sin efecto y el nuevo comenzará a revisarse desde cero.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700, color: "#666" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#f59e0b",
              "&:hover": { backgroundColor: "#d97706" },
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 999,
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Sí, reemplazar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}